import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/navigation';

import AppApi from '@/apis/app-api';
import UserApi from '@/apis/user.api';
import { baseUrl } from '@/consts/route';
import { UpdateUserDto, User } from '@/types/user';
import { cleanArray } from '@/utils/cleanArray';

import { useAuth } from './useAuth';

export const useUser = (initialData?: User) => {
  const { accessToken, email } = useAuth();
  const api = new AppApi(baseUrl);
  const userApi = new UserApi(api);

  const getUser = async () => {
    if (!accessToken) {
      return {} as User;
    }
    api.setup(accessToken);

    return (await userApi.getUserMe()) ?? ({} as User);
  };

  return useQuery({
    queryKey: cleanArray([email, 'user']),
    queryFn: getUser,
    enabled: !!accessToken && !!email,
    retry: 5,
    retryDelay: 1000,
    initialData,
  });
};

export const useUserCheckPassword = () => {
  const { accessToken, email } = useAuth();
  const api = new AppApi(baseUrl);
  const userApi = new UserApi(api);
  const queryClient = useQueryClient();

  const checkPassword = async (password: string) => {
    if (!accessToken) {
      return {} as any;
    }
    api.setup(accessToken);

    return await userApi.checkPassword({ password });
  };

  return useMutation({
    mutationKey: cleanArray([email ?? '', 'check-password']),
    mutationFn: async (password: string) => await checkPassword(password),
    onSuccess: async () => {
      await queryClient.invalidateQueries();
    },
  });
};

export const useUserUpdate = () => {
  const { accessToken, email } = useAuth();
  const queryClient = useQueryClient();
  const api = new AppApi(baseUrl);

  const userApi = new UserApi(api);

  const updateUser = async (body: UpdateUserDto) => {
    if (!accessToken) {
      return {} as any;
    }
    api.setup(accessToken);

    return await userApi.patchUser(body);
  };

  return useMutation({
    mutationKey: cleanArray([email, 'update']),
    mutationFn: async (body: UpdateUserDto) => await updateUser(body),
    onSuccess: async () => {
      await queryClient.refetchQueries({
        queryKey: cleanArray([email, 'user']),
      });
    },
  });
};

export const useUpdateBusinessNumber = () => {
  const { accessToken, email } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['update-business-number'],
    mutationFn: async (businessNumber: string) => {
      if (!accessToken) throw new Error('인증이 필요합니다.');

      const api = new AppApi(baseUrl);
      api.setup(accessToken);
      const userApi = new UserApi(api);

      return await userApi.updateBusinessNumber({ business_number: businessNumber });
    },
    onSuccess: async () => {
      await queryClient.refetchQueries({
        queryKey: cleanArray([email, 'user']),
      });
    },
  });
};

export const useUpdatePrimaryShop = () => {
  const { accessToken, userId, email } = useAuth();
  const queryClient = useQueryClient();
  const api = new AppApi(baseUrl);

  const userApi = new UserApi(api);

  const updateUser = async (shopId: string) => {
    if (!accessToken) {
      return {} as any;
    }
    api.setup(accessToken);

    return await userApi.updatePrimaryShop({ shopId, userId });
  };
  return useMutation({
    mutationFn: async (shopId: string) => await updateUser(shopId),
    mutationKey: cleanArray([userId, 'update-primary-shop']),
    onSuccess: async () => {
      await queryClient.refetchQueries({
        queryKey: cleanArray([email, 'user']),
      });
    },
  });
};

export const useDeleteUser = () => {
  const { accessToken } = useAuth();
  const router = useRouter();

  const _deleteUser = async () => {
    const api = new AppApi(baseUrl);
    const userApi = new UserApi(api);
    api.setup(accessToken);
    return await userApi.deleteUser();
  };

  return useMutation({
    mutationFn: async () => await _deleteUser(),
    mutationKey: ['delete-user'],
    onSuccess: () => {
      router.push('/auth/sign-out');
    },
  });
};
